const DEFAULT_STORE_TIMEZONE = 'Europe/Moscow';

const DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})/;
const HAS_TIMEZONE_RE = /[zZ]|[+-]\d{2}:?\d{2}$/;

type DateInput = string | number | Date;

const hasDateValue = (
  value: DateInput | null | undefined
): value is DateInput => {
  if (value == null) {
    return false;
  }

  return typeof value !== 'string' || value !== '';
};

const pad2 = (value: string | undefined): string => {
  if (!value) {
    return '00';
  }

  return value.length === 1 ? `0${value}` : value;
};

const calendarDateFromParts = (
  date: Date,
  timeZone: string
): string | false => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);

    const year = parts.find(part => part.type === 'year')?.value;
    const month = pad2(parts.find(part => part.type === 'month')?.value);
    const day = pad2(parts.find(part => part.type === 'day')?.value);

    if (!year) {
      return false;
    }

    return `${year}-${month}-${day}`;
  } catch {
    return false;
  }
};

const toStoreCalendarDate = (
  value: DateInput,
  timeZone: string
): string | false => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const prefix = DATE_PREFIX_RE.exec(trimmed)?.[1];

    if (prefix && !HAS_TIMEZONE_RE.test(trimmed)) {
      return prefix;
    }
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return calendarDateFromParts(date, timeZone);
};

const formatDayMonth = (calendarDate: string): string => {
  const segments = calendarDate.split('-');
  const month = segments[1];
  const day = segments[2];

  return `${day}.${month}`;
};

export const getPreorderDateLabel = (
  nearestDeliveryValue?: DateInput | null,
  productAvailableAt?: DateInput | null,
  options?: {
    isNearest?: boolean | null;
    timeZone?: string;
  }
): string | false => {
  if (options?.isNearest === true) {
    return false;
  }

  if (!hasDateValue(nearestDeliveryValue) || !hasDateValue(productAvailableAt)) {
    return false;
  }

  const timeZone = options?.timeZone || DEFAULT_STORE_TIMEZONE;
  const storeDate = toStoreCalendarDate(nearestDeliveryValue, timeZone);
  const productDate = toStoreCalendarDate(productAvailableAt, timeZone);

  if (!storeDate || !productDate) {
    return false;
  }

  if (
    typeof nearestDeliveryValue === 'string' &&
    typeof productAvailableAt === 'string'
  ) {
    const storePrefix = DATE_PREFIX_RE.exec(
      nearestDeliveryValue.trim()
    )?.[1];
    const productPrefix = DATE_PREFIX_RE.exec(
      productAvailableAt.trim()
    )?.[1];

    if (storePrefix && productPrefix && storePrefix === productPrefix) {
      return false;
    }
  }

  if (productDate > storeDate) {
    return formatDayMonth(productDate);
  }

  return false;
};
